import React from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { isDevelopment } from '../config/env';

// Exponential backoff for retries
const retryDelay = (attemptIndex: number) => {
  return Math.min(1000 * 2 ** attemptIndex, 30000); // Max 30 seconds
};

// Custom error handler for global errors
const onError = (error: Error) => {
  if (isDevelopment) {
    console.error('React Query Error:', error);
  }
  // In production, could send to error tracking service like Sentry
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Retry configuration with exponential backoff
      retry: (failureCount, error: Error & { statusCode?: number }) => {
        // Don't retry on 4xx errors (client errors)
        if (error?.statusCode && error.statusCode >= 400 && error.statusCode < 500) {
          return false;
        }
        // Retry up to 3 times for network/server errors
        return failureCount < 3;
      },
      retryDelay,

      // Cache timing - optimized for data freshness vs performance
      staleTime: 5 * 60 * 1000, // 5 minutes - data considered fresh
      gcTime: 30 * 60 * 1000, // 30 minutes - unused cache garbage collected

      // Refetch configuration - balance freshness with performance
      refetchOnWindowFocus: true, // Refetch when user returns to tab
      refetchOnReconnect: true, // Refetch when internet reconnects
      refetchOnMount: true, // Refetch on component mount if stale
      refetchInterval: false, // Disable polling by default

      // Network mode - what to do when offline
      networkMode: 'online', // Only fetch when online

      // Error handling
      throwOnError: false, // Don't throw errors, let components handle

      // Suspense
      suspense: false, // Disable React Suspense mode by default
    },
    mutations: {
      // Mutations should not retry by default (user action)
      retry: false,

      // Network mode for mutations
      networkMode: 'online',

      // Global error handler for mutations
      onError,
    },
  },
});

interface QueryProviderProps {
  children: React.ReactNode;
}

export default function QueryProvider({ children }: QueryProviderProps) {
  return (
    <QueryClientProvider client={queryClient}>
      {children}
      {isDevelopment && (
        <ReactQueryDevtools 
          initialIsOpen={false} 
          position="bottom-right"
        />
      )}
    </QueryClientProvider>
  );
}